import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { URLS } from "src/_config";

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NotificationItem {
  id: number;
  user_id: number;
  company_id: number;
  action: string;
  action_name: string;
  details: string;
  ip_address: string;
  user_agent: string;
  request_method: string;
  request_url: string;
  request_data: any;
  response_status: number;
  is_notified: number;
  is_read: number;
  created_at: string;
  updated_at: string;
}

interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export default function NotificationPanel({ isOpen, onClose }: NotificationPanelProps) {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const { t } = useTranslation("sidebar");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const history = useHistory();

  const userApi = useUserApi();
  const toast = useToaster();

  // Fetch notifications when panel opens - only first 6
  useEffect(() => {
    if (isOpen) {
      setNotifications([]);
      fetchNotifications();
    }
  }, [isOpen]);

  const fetchNotifications = () => {
    setLoading(true);

    const params = {
      per_page: 6, // Only fetch first 6 notifications
      page: 1,
    };

    userApi.callGetNotifications(
      params,
      (message: string, resp: any) => {
        console.log("Notifications fetched successfully:", resp);

        // Handle the API response structure based on new format
        let newNotifications: NotificationItem[] = [];
        let paginationMeta: PaginationMeta | null = null;

        if (resp) {
          // New structure: resp.user_activities contains the notifications array
          if (Array.isArray(resp.user_activities)) {
            newNotifications = resp.user_activities;
          }
          // New structure: resp.pagination contains the pagination info
          if (resp.pagination) {
            paginationMeta = resp.pagination;
          }
        }

        setNotifications(newNotifications);

        if (paginationMeta) {
          setMeta(paginationMeta);
        }

        setLoading(false);
      },
      (error: string) => {
        console.error("Error fetching notifications:", error);
        toast.error("Failed to fetch notifications: " + error);
        setNotifications([]);
        setLoading(false);
      }
    );
  };

  // Helper function to get icon and color based on notification action
  const getNotificationStyle = (action: string, isRead: number) => {
    const baseOpacity = isRead === 1 ? 0.6 : 1;
    const actionLower = action.toLowerCase();

    if (actionLower.includes("interview")) {
      return {
        icon: "fa-video-camera",
        color: "#f59e0b",
        bgColor: `rgba(245, 158, 11, ${baseOpacity * 0.1})`,
      };
    } else if (actionLower.includes("schedule")) {
      return {
        icon: "fa-calendar",
        color: "#8b5cf6",
        bgColor: `rgba(139, 92, 246, ${baseOpacity * 0.1})`,
      };
    } else if (actionLower.includes("approved") || actionLower.includes("accepted")) {
      return {
        icon: "fa-check-circle",
        color: "#10b981",
        bgColor: `rgba(16, 185, 129, ${baseOpacity * 0.1})`,
      };
    } else if (actionLower.includes("rejected") || actionLower.includes("declined")) {
      return {
        icon: "fa-times-circle",
        color: "#ef4444",
        bgColor: `rgba(239, 68, 68, ${baseOpacity * 0.1})`,
      };
    } else if (actionLower.includes("application")) {
      return {
        icon: "fa-file-text",
        color: "#3b82f6",
        bgColor: `rgba(59, 130, 246, ${baseOpacity * 0.1})`,
      };
    } else if (actionLower.includes("offer")) {
      return {
        icon: "fa-gift",
        color: "#ec4899",
        bgColor: `rgba(236, 72, 153, ${baseOpacity * 0.1})`,
      };
    } else {
      return {
        icon: "fa-bell",
        color: "#6b7280",
        bgColor: `rgba(107, 114, 128, ${baseOpacity * 0.1})`,
      };
    }
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleNotificationClick = (notification: NotificationItem) => {
    // Handle navigation based on notification request_data if needed
    // Currently no navigation as the API doesn't provide URL
  };

  if (!isOpen) return null;

  return (
    <div className="application-history-overlay">
      <div className="application-history-sidebar">
        <div className="history-header">
          <h3>{t("sidebar.notifications.title")}</h3>
          <p>{t("sidebar.notifications.subtitle")}</p>
          <button className="close-btn" onClick={onClose}>
            <i className="fa fa-times"></i>
          </button>
        </div>

        <div
          className="history-timeline"
          ref={scrollContainerRef}
          style={{ overflowY: "auto", maxHeight: "calc(100vh - 120px)" }}
        >
          {loading ? (
            <div className="loading-container" style={{ padding: "40px 20px", textAlign: "center" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "15px",
                  marginBottom: "10px",
                }}
              >
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    border: "3px solid #f3f3f3",
                    borderTop: "3px solid #3498db",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
                <span style={{ fontSize: "16px", color: "#666" }}>{t("sidebar.notifications.loading")}</span>
              </div>
            </div>
          ) : notifications.length > 0 ? (
            notifications.map((notification, index) => {
              const style = getNotificationStyle(notification.action, notification.is_read);
              return (
                <div
                  key={notification.id}
                  className="timeline-item"
                  onClick={() => handleNotificationClick(notification)}
                  style={{
                    cursor: "pointer",
                    backgroundColor: notification.is_read === 0 ? style.bgColor : "transparent",
                    borderRadius: "8px",
                    padding: "12px",
                    marginBottom: "8px",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = style.bgColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = notification.is_read === 0 ? style.bgColor : "transparent";
                  }}
                >
                  <div className="timeline-icon-wrapper">
                    <div
                      className="timeline-icon"
                      style={{
                        backgroundColor: style.color,
                        opacity: notification.is_read === 1 ? 0.6 : 1,
                      }}
                    >
                      <i className={`fa ${style.icon}`}></i>
                    </div>
                  </div>
                  <div className="timeline-details">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <h4
                        className="timeline-title"
                        style={{
                          fontWeight: notification.is_read === 0 ? "bold" : "normal",
                          opacity: notification.is_read === 1 ? 0.7 : 1,
                        }}
                      >
                        {notification.action}
                      </h4>
                      {notification.is_read === 0 && (
                        <span
                          style={{
                            width: "8px",
                            height: "8px",
                            backgroundColor: style.color,
                            borderRadius: "50%",
                            display: "inline-block",
                            marginLeft: "10px",
                            flexShrink: 0,
                          }}
                        ></span>
                      )}
                    </div>
                    <span
                      className="timeline-date"
                      style={{
                        opacity: notification.is_read === 1 ? 0.5 : 0.7,
                      }}
                    >
                      {formatDate(notification.created_at)}
                    </span>
                    <p
                      className="timeline-description"
                      style={{
                        opacity: notification.is_read === 1 ? 0.6 : 1,
                      }}
                    >
                      {notification.details}
                    </p>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-history" style={{ padding: "40px 20px", textAlign: "center", color: "#666" }}>
              <i className="fa fa-bell-slash" style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.5 }}></i>
              <p>{t("sidebar.notifications.noNotifications")}</p>
            </div>
          )}

          {/* View All Button */}
          {!loading && notifications.length > 0 && (
            <div style={{ padding: "20px", textAlign: "center", borderTop: "1px solid #e5e7eb" }}>
              <button
                className="btn-preview"
                onClick={() => {
                  onClose();
                  history.push(URLS.NOTIFICATIONS.MAIN);
                }}
              >
                {t("sidebar.notifications.viewAll")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
